import { useEffect, useState } from "react";
import { Search, Plus, X, User, Car, Save, Loader2, History, MapPin } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PhoneField } from "@/components/PhoneField";
import { useLang } from "@/context/LanguageContext";

const STATUS_CLS = {
  pending: "text-amber-400", quoted: "text-fuchsia-400", confirmed: "text-[#00E5FF]",
  completed: "text-emerald-400", cancelled: "text-zinc-500", declined: "text-[#FF1A1A]",
};

export function ClientsView() {
  const { t, tf } = useLang();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newC, setNewC] = useState({ name: "", email: "", phone: "" });

  const load = () => {
    setLoading(true);
    api.get("/clients").then(({ data }) => setClients(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openClient = (c) => { setSelected(c); setNotes(c.notes || ""); };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.patch("/clients", { email: selected.email, notes });
      toast.success(t("admin.cl_saved"));
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addClient = async () => {
    if (!newC.name || !newC.email) { toast.error(t("admin.cl_name") + " + " + t("admin.cl_email")); return; }
    try {
      await api.post("/clients", newC);
      toast.success(t("admin.cl_saved"));
      setAddOpen(false); setNewC({ name: "", email: "", phone: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = clients.filter((c) => `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="clients-view">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search" placeholder={t("admin.cl_search")} className="bg-[#0C0C0E] border-white/10 rounded-none h-11 pl-10 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0" />
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <button data-testid="clients-add-btn" className="inline-flex items-center gap-2 px-5 h-11 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors"><Plus className="w-4 h-4" /> {t("admin.cl_add")}</button>
          </DialogTrigger>
          <DialogContent className="bg-[#0C0C0E] border border-white/10 text-white rounded-none max-w-md">
            <DialogHeader><DialogTitle className="font-display font-900 uppercase tracking-tight">{t("admin.cl_add")}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <Input value={newC.name} onChange={(e) => setNewC((c) => ({ ...c, name: e.target.value }))} placeholder={t("admin.cl_name")} data-testid="new-client-name" className="bg-[#050505] border-white/10 rounded-none h-11 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0" />
              <Input type="email" value={newC.email} onChange={(e) => setNewC((c) => ({ ...c, email: e.target.value }))} placeholder={t("admin.cl_email")} data-testid="new-client-email" className="bg-[#050505] border-white/10 rounded-none h-11 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0" />
              <PhoneField value={newC.phone} onChange={(v) => setNewC((c) => ({ ...c, phone: v }))} testId="new-client-phone" />
              <button onClick={addClient} data-testid="new-client-save" className="w-full h-11 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors">{t("admin.cl_save")}</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="border border-white/10 bg-[#0C0C0E] py-16 text-center text-zinc-500 font-mono-tech text-sm" data-testid="clients-empty">{t("admin.cl_none")}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {filtered.map((c) => (
            <button key={c.email} onClick={() => openClient(c)} data-testid={`client-card-${c.email}`} className="bg-[#0C0C0E] p-5 text-left hover:bg-[#121215] transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center border border-[#E60000]/40 bg-[#E60000]/10 text-[#E60000] font-display font-900">{c.name?.[0]?.toUpperCase() || "?"}</div>
                <div className="min-w-0">
                  <div className="font-display font-700 text-white truncate">{c.name}</div>
                  <div className="font-mono-tech text-[11px] text-zinc-500 truncate">{c.email}</div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5 font-mono-tech text-[11px]">
                <span className="text-[#00E5FF]">{c.visits} {t("admin.cl_visits")}</span>
                <span className="text-zinc-500">{c.last_date || "—"}</span>
              </div>
              {c.vehicle?.marque && <div className="flex items-center gap-1.5 mt-2 text-zinc-600 text-[11px] font-mono-tech"><Car className="w-3 h-3" /> {c.vehicle.marque} {c.vehicle.modele}</div>}
            </button>
          ))}
        </div>
      )}

      {/* Client detail */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#0C0C0E] border border-white/10 text-white rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-900 uppercase tracking-tight flex items-center gap-2"><User className="w-5 h-5 text-[#E60000]" /> {selected.name}</DialogTitle>
              </DialogHeader>
              <div className="font-mono-tech text-sm space-y-1 mt-1">
                <div className="text-zinc-400">{selected.email}</div>
                <div className="text-zinc-400">{selected.phone || "—"}</div>
                {selected.address && <div className="flex items-start gap-1.5 text-zinc-300"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#E60000]" /> {selected.address}</div>}
                {selected.vehicle?.marque && <div className="flex items-center gap-1.5 text-[#00E5FF]"><Car className="w-3.5 h-3.5" /> {selected.vehicle.marque} {selected.vehicle.modele} {selected.vehicle.immatriculation ? `· ${selected.vehicle.immatriculation}` : ""}</div>}
              </div>

              <div className="mt-4">
                <label className="font-mono-tech text-[10px] uppercase tracking-wider text-[#00E5FF] block mb-2">{t("admin.cl_notes")}</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="client-notes" className="bg-[#050505] border-white/10 rounded-none text-white text-sm focus-visible:border-[#00E5FF] focus-visible:ring-0 min-h-[80px]" />
                <button onClick={saveNotes} disabled={saving} data-testid="client-save-notes" className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide text-sm border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("admin.cl_save")}
                </button>
              </div>

              <div className="mt-5">
                <div className="flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 mb-2"><History className="w-3.5 h-3.5" /> {t("admin.cl_history")}</div>
                <div className="space-y-px bg-white/10 border border-white/10">
                  {selected.history.map((h) => (
                    <div key={h.id} className="bg-[#050505] p-2.5 flex items-center justify-between font-mono-tech text-xs">
                      <span className="text-white">{tf(`services.${h.service_id}.label`, h.service_label)}</span>
                      <span className="text-zinc-500">{h.date} · {h.time}</span>
                      <span className={STATUS_CLS[h.status]}>{t(`status.${h.status}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
